import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { RefreshCw, Plane, Flame, MessageCircle, AlertTriangle, Radio, Maximize2, Minimize2, Play, Pause, RotateCcw, X, Clock, Ship } from 'lucide-react';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import { createMilitaryIcon, getAircraftColor, LEGEND_ITEMS } from './map/aircraftIcons';
import ExplosionMarker, { EXPLOSION_CSS } from './map/ExplosionMarker';
import SourceConsole from './map/SourceConsole';
import SatelliteLayer, { SATELLITE_CSS } from './map/SatelliteLayer';
import ConflictEventsFeed from './map/ConflictEventsFeed';

const API = process.env.REACT_APP_BACKEND_URL;

const THEATERS = {
  ormuz: { center: [26.5, 56.5], zoom: 6, label: "Detroit d'Ormuz" },
  mer_rouge: { center: [18, 40], zoom: 6, label: "Mer Rouge" },
  mer_de_chine: { center: [15, 115], zoom: 5, label: "Mer de Chine" },
  mer_noire: { center: [43, 35], zoom: 6, label: "Mer Noire" },
  polynesie: { center: [-17, -149], zoom: 6, label: "Polynesie" },
};

const FIRMS_COLORS = { low: '#fbbf24', medium: '#f97316', high: '#ef4444', extreme: '#dc2626' };
const TIME_WINDOWS = [1, 3, 6, 12, 24];
// Seconds of playback per real hour of data
const SPEED_OPTIONS = [2, 3, 5, 8, 15];

function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => { map.setView(center, zoom, { animate: true }); }, [center, zoom, map]);
  return null;
}

function getTrajectoryTrail(lat, lon, heading, speed, points = 12) {
  const trail = [];
  const rev = ((heading + 180) % 360) * (Math.PI / 180);
  const step = Math.max(0.02, (speed || 300) / 15000);
  for (let i = 1; i <= points; i++) {
    trail.push([lat + step * i * Math.cos(rev), lon + step * i * Math.sin(rev)]);
  }
  return [[lat, lon], ...trail];
}

function formatTime(iso) {
  if (!iso) return '--:--';
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '--:--'; }
}

export default function TheaterMap({ token, currentTheater = 'ormuz' }) {
  // Live state
  const [firms, setFirms] = useState({ events: [], total: 0, high_intensity: 0, possible_explosions: 0 });
  const [flights, setFlights] = useState({ flights: [], military: 0 });
  const [telegram, setTelegram] = useState({ posts: [], total_posts: 0 });
  const [loading, setLoading] = useState({});
  const [showLayers, setShowLayers] = useState({ firms: true, flights: true, telegram: true, trails: true, satellites: true, osintFeed: true, vessels: true });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [conflictMarkers, setConflictMarkers] = useState([]);
  const [vessels, setVessels] = useState({ vessels: [], count: 0, source_label: '' });

  // Replay state
  const [replayMode, setReplayMode] = useState(false);
  const [replayData, setReplayData] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySecsPerHour, setReplaySecsPerHour] = useState(5);
  const [replayTimeWindow, setReplayTimeWindow] = useState(12);
  const [replayProgress, setReplayProgress] = useState(0);
  const [replayFrame, setReplayFrame] = useState(0);
  const [replayCurrentTime, setReplayCurrentTime] = useState(null);
  const [replayConsoleEvents, setReplayConsoleEvents] = useState([]);
  const [activeExplosions, setActiveExplosions] = useState([]);
  const [satelliteData, setSatelliteData] = useState([]);
  const replayTimerRef = useRef(null);
  const consoleEventsRef = useRef([]);

  const theaterKey = Object.keys(THEATERS).find(k =>
    currentTheater?.toLowerCase().includes(k.replace('_', ' ').replace('mer ', ''))
  ) || 'ormuz';
  const theater = THEATERS[theaterKey] || THEATERS.ormuz;

  // --- LIVE MODE ---
  const fetchAllLayers = useCallback(async () => {
    setLoading({ firms: true, flights: true, telegram: true });
    setError(null);
    try {
      const res = await axios.get(`${API}/api/map/layers?theater=${theaterKey}`, authHeaders);
      if (res.data.firms) setFirms(res.data.firms);
      if (res.data.flights) setFlights(res.data.flights);
      if (res.data.telegram) setTelegram(res.data.telegram);
    } catch { setError('Erreur de chargement'); }
    setLoading({});
  }, [theaterKey, token]);

  useEffect(() => { if (!replayMode) fetchAllLayers(); }, [theaterKey, replayMode]);

  // --- VESSELS (AIS Maritime) ---
  useEffect(() => {
    if (!showLayers.vessels || !token) return;
    axios.get(`${API}/api/maritime/vessels?theater=${theaterKey}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data) setVessels(res.data); })
      .catch(err => console.warn('[AIS] Vessel fetch error:', err?.message));
  }, [theaterKey, showLayers.vessels, token]);

  // --- SATELLITES ---
  useEffect(() => {
    if (!showLayers.satellites) return;
    axios.get(`${API}/api/map/satellites`, authHeaders)
      .then(res => { if (res.data?.satellites) setSatelliteData(res.data.satellites); })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (replayMode) return;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/map/flights?theater=${theaterKey}`, authHeaders);
        setFlights(res.data);
      } catch {}
    }, 30000);
    return () => clearInterval(iv);
  }, [theaterKey, token, replayMode]);

  // --- FULLSCREEN ---
  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!isFullscreen) {
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  // --- REPLAY FUNCTIONS ---
  const loadReplayData = useCallback(async (hours) => {
    setReplayLoading(true);
    setReplayPlaying(false);
    setReplayProgress(0);
    setReplayFrame(0);
    setReplayConsoleEvents([]);
    consoleEventsRef.current = [];
    setActiveExplosions([]);
    try {
      const res = await axios.get(`${API}/api/map/replay?theater=${theaterKey}&hours=${hours}`, authHeaders);
      setReplayData(res.data);
      setReplayCurrentTime(res.data?.start_time);
      // Auto-start playback once data is loaded
      setTimeout(() => setReplayPlaying(true), 500);
    } catch {
      setError('Erreur chargement replay');
    }
    setReplayLoading(false);
  }, [theaterKey, token]);

  const enterReplayMode = () => {
    setReplayMode(true);
    loadReplayData(replayTimeWindow);
  };
  const exitReplayMode = () => {
    setReplayMode(false);
    setReplayPlaying(false);
    setReplayData(null);
    setReplayConsoleEvents([]);
    consoleEventsRef.current = [];
    setActiveExplosions([]);
    if (replayTimerRef.current) clearInterval(replayTimerRef.current);
  };
  const handleTimeWindowChange = (hours) => {
    setReplayTimeWindow(hours);
    if (replayMode) loadReplayData(hours);
  };

  // --- REPLAY PLAYBACK ENGINE ---
  useEffect(() => {
    if (!replayPlaying || !replayData) {
      if (replayTimerRef.current) clearInterval(replayTimerRef.current);
      return;
    }
    const totalFrames = replayData.adsb_timeline?.length || 1;
    // Calculate interval: total play duration = secsPerHour * hours
    const totalPlayMs = replaySecsPerHour * replayData.time_window_hours * 1000;
    const intervalMs = Math.max(30, totalPlayMs / totalFrames);

    replayTimerRef.current = setInterval(() => {
      setReplayFrame(prev => {
        const next = prev + 1;
        if (next >= totalFrames) {
          setReplayPlaying(false);
          return totalFrames - 1;
        }
        const progress = next / (totalFrames - 1);
        setReplayProgress(progress);

        const startMs = new Date(replayData.start_time).getTime();
        const endMs = new Date(replayData.end_time).getTime();
        const currentMs = startMs + progress * (endMs - startMs);
        const currentTimeStr = new Date(currentMs).toISOString();
        setReplayCurrentTime(currentTimeStr);

        // Feed events to console
        const newEvents = [...consoleEventsRef.current];
        let changed = false;

        replayData.telegram_posts?.forEach((post, i) => {
          const pid = `tg-${i}`;
          if (newEvents.some(e => e.id === pid)) return;
          try {
            if (new Date(post.date).getTime() <= currentMs) {
              newEvents.push({ ...post, type: 'telegram', id: pid });
              changed = true;
            }
          } catch {}
        });

        replayData.firms_events?.forEach((ev, i) => {
          const eid = `firms-${i}`;
          if (newEvents.some(e => e.id === eid)) return;
          try {
            if (new Date(ev.datetime).getTime() <= currentMs) {
              const evType = ev.possible_explosion ? 'explosion' : 'firms';
              newEvents.push({ ...ev, type: evType, id: eid });
              changed = true;
              if (ev.possible_explosion) {
                // Find attributed aircraft position at this frame
                let acPos = null;
                if (ev.attribution?.lat && ev.attribution?.lon) {
                  acPos = [ev.attribution.lat, ev.attribution.lon];
                } else {
                  // Try to find aircraft from current frame
                  const curFlights = replayData.adsb_timeline?.[next]?.flights || [];
                  for (const f of curFlights) {
                    if (f.origin && f.origin !== 'Unknown') {
                      const d = Math.sqrt((ev.lat - f.lat) ** 2 + (ev.lon - f.lon) ** 2);
                      if (d < 3) { acPos = [f.lat, f.lon]; break; }
                    }
                  }
                }
                setActiveExplosions(prev => [...prev, {
                  ...ev, key: eid, active: true,
                  aircraftPos: acPos, datetime: ev.datetime,
                }]);
              }
            }
          } catch {}
        });

        if (changed) {
          consoleEventsRef.current = newEvents;
          setReplayConsoleEvents([...newEvents]);
        }
        return next;
      });
    }, intervalMs);

    return () => clearInterval(replayTimerRef.current);
  }, [replayPlaying, replayData, replaySecsPerHour]);

  // Seek
  const handleSeek = (pct) => {
    if (!replayData) return;
    const totalFrames = replayData.adsb_timeline?.length || 1;
    const frame = Math.round(pct * (totalFrames - 1));
    setReplayFrame(frame);
    setReplayProgress(pct);
    const startMs = new Date(replayData.start_time).getTime();
    const endMs = new Date(replayData.end_time).getTime();
    const curMs = startMs + pct * (endMs - startMs);
    setReplayCurrentTime(new Date(curMs).toISOString());

    // Rebuild console events up to this point
    const eventsUpToNow = [];
    replayData.telegram_posts?.forEach((post, i) => {
      try { if (new Date(post.date).getTime() <= curMs) eventsUpToNow.push({ ...post, type: 'telegram', id: `tg-${i}` }); } catch {}
    });
    replayData.firms_events?.forEach((ev, i) => {
      try { if (new Date(ev.datetime).getTime() <= curMs) eventsUpToNow.push({ ...ev, type: ev.possible_explosion ? 'explosion' : 'firms', id: `firms-${i}` }); } catch {}
    });
    consoleEventsRef.current = eventsUpToNow;
    setReplayConsoleEvents([...eventsUpToNow]);
  };

  // Reset
  const handleReset = () => {
    setReplayFrame(0);
    setReplayProgress(0);
    setReplayCurrentTime(replayData?.start_time);
    setReplayConsoleEvents([]);
    consoleEventsRef.current = [];
    setActiveExplosions([]);
    setReplayPlaying(false);
  };

  // Current data for rendering
  const currentFlights = replayMode && replayData
    ? (replayData.adsb_timeline?.[replayFrame]?.flights || [])
    : (flights.flights || []);
  const currentFirms = replayMode && replayData
    ? (replayData.firms_events || [])
    : (firms.events || []);

  const isAnyLoading = Object.values(loading).some(Boolean);

  return (
    <div ref={containerRef} className={`flex flex-col bg-black ${isFullscreen ? 'h-screen' : 'h-full'}`} data-testid="theater-map">
      {/* ============ TOP CONTROL BAR ============ */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-black/95 border-b border-cyan-500/20 flex-shrink-0 z-10 gap-2 flex-wrap">
        {/* Left: Mode + Theater */}
        <div className="flex items-center gap-2">
          <div className={`px-2 py-0.5 font-mono text-xs font-bold uppercase tracking-wider border ${
            replayMode ? 'text-amber-400 border-amber-500/50 bg-amber-500/10' : 'text-green-400 border-green-500/50 bg-green-500/10'
          }`}>
            {replayMode ? 'REPLAY' : 'LIVE'}
          </div>
          <span className="font-mono text-cyan-400 text-xs uppercase tracking-wider">{theater.label}</span>
          {replayMode && replayData?.simulated_flights && (
            <span className="px-2 py-0.5 font-mono text-[10px] border border-yellow-500/40 text-yellow-500 bg-yellow-500/10">
              DONNEES SIMULEES
            </span>
          )}
        </div>

        {/* Center: Live toggles OR Replay controls */}
        {!replayMode ? (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setShowLayers(l => ({ ...l, flights: !l.flights }))}
              className={`px-2 py-1 font-mono text-xs border flex items-center gap-1 transition-colors ${showLayers.flights ? 'bg-sky-500/20 border-sky-500 text-sky-400' : 'border-white/10 text-white/30'}`}
              data-testid="toggle-flights">
              <Plane className="w-3 h-3" /> ADS-B ({flights.military || 0})
            </button>
            <button onClick={() => setShowLayers(l => ({ ...l, trails: !l.trails }))}
              className={`px-2 py-1 font-mono text-xs border flex items-center gap-1 transition-colors ${showLayers.trails ? 'bg-violet-500/20 border-violet-500 text-violet-400' : 'border-white/10 text-white/30'}`}
              data-testid="toggle-trails">Trajectoires</button>
            <button onClick={() => setShowLayers(l => ({ ...l, firms: !l.firms }))}
              className={`px-2 py-1 font-mono text-xs border flex items-center gap-1 transition-colors ${showLayers.firms ? 'bg-orange-500/20 border-orange-500 text-orange-400' : 'border-white/10 text-white/30'}`}
              data-testid="toggle-firms">
              <Flame className="w-3 h-3" /> FIRMS ({firms.total || 0})
            </button>
            <button onClick={() => setShowLayers(l => ({ ...l, telegram: !l.telegram }))}
              className={`px-2 py-1 font-mono text-xs border flex items-center gap-1 transition-colors ${showLayers.telegram ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'border-white/10 text-white/30'}`}
              data-testid="toggle-telegram">
              <MessageCircle className="w-3 h-3" /> Telegram ({telegram.total_posts || 0})
            </button>
            <button onClick={() => setShowLayers(l => ({ ...l, satellites: !l.satellites }))}
              className={`px-2 py-1 font-mono text-xs border flex items-center gap-1 transition-colors ${showLayers.satellites ? 'bg-purple-500/20 border-purple-500 text-purple-400' : 'border-white/10 text-white/30'}`}
              data-testid="toggle-satellites">
              <Radio className="w-3 h-3" /> Satellites ({satelliteData.length || 0})
            </button>
            <button onClick={() => setShowLayers(l => ({ ...l, osintFeed: !l.osintFeed }))}
              className={`px-2 py-1 font-mono text-xs border flex items-center gap-1 transition-colors ${showLayers.osintFeed ? 'bg-red-500/20 border-red-500 text-red-400' : 'border-white/10 text-white/30'}`}
              data-testid="toggle-osint-feed">
              <AlertTriangle className="w-3 h-3" /> OSINT Feed
            </button>
            <button onClick={() => setShowLayers(l => ({ ...l, vessels: !l.vessels }))}
              className={`px-2 py-1 font-mono text-xs border flex items-center gap-1 transition-colors ${showLayers.vessels ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400' : 'border-white/10 text-white/30'}`}
              data-testid="toggle-vessels">
              <Ship className="w-3 h-3" /> AIS ({vessels.count || 0})
            </button>
          </div>
        ) : (
          /* Replay Controls — inline in top bar */
          <div className="flex items-center gap-2" data-testid="replay-controls">
            {/* Time windows */}
            <div className="flex items-center gap-0.5">
              <Clock className="w-3 h-3 text-cyan-400/50 mr-1" />
              {TIME_WINDOWS.map(tw => (
                <button key={tw} onClick={() => handleTimeWindowChange(tw)} disabled={replayLoading}
                  className={`px-2 py-0.5 font-mono text-xs border transition-all ${
                    replayTimeWindow === tw
                      ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300 shadow-[0_0_6px_rgba(0,200,255,0.2)]'
                      : 'border-white/10 text-white/30 hover:text-white/60'
                  }`} data-testid={`replay-window-${tw}h`}>
                  {tw}H
                </button>
              ))}
            </div>
            <div className="w-px h-5 bg-white/10" />
            {/* Playback */}
            <button onClick={handleReset} disabled={replayLoading}
              className="p-1 text-white/40 hover:text-cyan-400 transition-colors" data-testid="replay-reset" title="Retour au debut">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setReplayPlaying(p => !p)} disabled={replayLoading}
              className={`p-1.5 border transition-all ${
                replayPlaying ? 'bg-red-500/20 border-red-500 text-red-400' : 'bg-cyan-500/20 border-cyan-400 text-cyan-400'
              }`} data-testid="replay-play-pause">
              {replayLoading ? <div className="w-3.5 h-3.5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                : replayPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            {/* Speed selector */}
            <button onClick={() => {
              const idx = SPEED_OPTIONS.indexOf(replaySecsPerHour);
              setReplaySecsPerHour(SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length]);
            }}
              className="px-2 py-0.5 border border-white/10 text-amber-400 font-mono text-xs hover:border-amber-400/50 transition-colors min-w-[48px] text-center"
              data-testid="replay-speed" title="Secondes par heure reelle">
              {replaySecsPerHour}s/h
            </button>
            {/* Current time */}
            <span className="font-mono text-cyan-300 text-sm font-bold tracking-wider min-w-[40px]">
              {formatTime(replayCurrentTime)}
            </span>
            {replayData && (
              <span className="font-mono text-white/20 text-[10px]">
                {replayFrame + 1}/{replayData.adsb_frame_count}
              </span>
            )}
          </div>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          {!replayMode && firms.possible_explosions > 0 && (
            <span className="px-2 py-0.5 bg-red-900/50 border border-red-500 text-red-400 font-mono text-xs flex items-center gap-1 animate-pulse">
              <AlertTriangle className="w-3 h-3" /> {firms.possible_explosions} point(s) chaud(s)
            </span>
          )}
          {!replayMode ? (
            <button onClick={enterReplayMode}
              className="px-3 py-1 border border-cyan-500/50 text-cyan-400 font-mono text-xs hover:bg-cyan-500/10 flex items-center gap-1"
              data-testid="enter-replay">
              <Play className="w-3 h-3" /> REPLAY
            </button>
          ) : (
            <button onClick={exitReplayMode}
              className="px-3 py-1 border border-red-500/50 text-red-400 font-mono text-xs hover:bg-red-500/10 flex items-center gap-1"
              data-testid="exit-replay">
              <X className="w-3 h-3" /> LIVE
            </button>
          )}
          {!replayMode && (
            <button onClick={fetchAllLayers} disabled={isAnyLoading}
              className="p-1 border border-white/10 text-white/50 hover:text-cyan-400 transition-colors" data-testid="refresh-map">
              <RefreshCw className={`w-3.5 h-3.5 ${isAnyLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button onClick={toggleFullscreen}
            className="p-1 border border-white/10 text-white/50 hover:text-amber-400 transition-colors" data-testid="toggle-fullscreen">
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* ============ REPLAY TIMELINE BAR ============ */}
      {replayMode && (
        <div className="px-3 py-1 bg-black/90 border-b border-white/5 flex items-center gap-2 flex-shrink-0 z-10">
          <span className="font-mono text-white/30 text-[10px] min-w-[60px]">{formatTime(replayData?.start_time)}</span>
          <div className="flex-1 relative h-1.5 cursor-pointer group" onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            handleSeek(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
          }}>
            <div className="absolute inset-0 bg-white/5 rounded-full" />
            <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-75"
              style={{ width: `${replayProgress * 100}%`, background: 'linear-gradient(90deg, #06b6d4, #22d3ee)' }} />
            <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-cyan-400 rounded-full shadow-[0_0_8px_rgba(0,200,255,0.6)] transition-[left] duration-75 group-hover:scale-150"
              style={{ left: `calc(${replayProgress * 100}% - 5px)` }} />
          </div>
          <span className="font-mono text-white/30 text-[10px] min-w-[60px] text-right">{formatTime(replayData?.end_time)}</span>
        </div>
      )}

      {/* ============ MAP + SIDEBAR ============ */}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 relative">
          {error && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-red-900/90 border border-red-500 text-red-300 px-3 py-1 font-mono text-xs">
              {error}
            </div>
          )}
          <MapContainer center={theater.center} zoom={theater.zoom}
            className="h-full w-full" style={{ background: '#0a0e1a' }} zoomControl={true}>
            <MapUpdater center={theater.center} zoom={theater.zoom} />
            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='OSM+CARTO' />

            {/* FIRMS thermal markers */}
            {(replayMode || showLayers.firms) && currentFirms.map((ev, i) => {
              if (replayMode && replayCurrentTime) {
                try {
                  if (new Date(ev.datetime).getTime() > new Date(replayCurrentTime).getTime()) return null;
                } catch {}
              }
              const isExplosion = ev.possible_explosion;
              return (
                <CircleMarker key={`firms-${i}`} center={[ev.lat, ev.lon]}
                  radius={isExplosion ? 10 : ev.intensity === 'extreme' ? 7 : ev.intensity === 'high' ? 5 : 3}
                  fillColor={isExplosion ? '#ff0000' : FIRMS_COLORS[ev.intensity] || '#fbbf24'}
                  fillOpacity={isExplosion ? 0.9 : 0.7}
                  stroke={isExplosion}
                  color={isExplosion ? '#ff0000' : '#ff6600'}
                  weight={isExplosion ? 3 : 1}
                  opacity={isExplosion ? 1 : 0.5}>
                  <Popup>
                    <div style={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: 12, minWidth: 200 }}>
                      <strong style={{ fontSize: 14, color: isExplosion ? '#ff4444' : '#fbbf24' }}>
                        {isExplosion ? 'POINT CHAUD INTENSE' : `Detection ${ev.intensity}`}
                      </strong><br />
                      {ev.city && <><span style={{ fontWeight: 'bold', color: '#ffffff' }}>{ev.city}</span><br /></>}
                      FRP: {ev.frp} MW | {ev.brightness}K<br />
                      {ev.attribution?.origin && (
                        <><strong style={{ color: '#ff6666' }}>Attribution: {ev.attribution.origin}</strong><br /></>
                      )}
                      <span style={{ color: '#94a3b8' }}>
                        {ev.datetime ? new Date(ev.datetime).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span><br />
                      <span style={{ color: '#64748b', fontSize: 10 }}>Source: {ev.source_info || 'NASA FIRMS VIIRS'}</span>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

            {/* Explosion Animations (Replay) */}
            {replayMode && activeExplosions.map(exp => (
              <ExplosionMarker key={exp.key} lat={exp.lat} lon={exp.lon}
                attribution={exp.attribution} isActive={exp.active}
                aircraftPos={exp.aircraftPos} datetime={exp.datetime}
                city={exp.city} sourceInfo={exp.source_info} />
            ))}

            {/* Military flights with SVG icons */}
            {(replayMode || showLayers.flights) && currentFlights.map((f, i) => {
              const color = getAircraftColor(f.type);
              const trail = (!replayMode && showLayers.trails) ? getTrajectoryTrail(f.lat, f.lon, f.heading, f.speed) : null;
              return (
                <React.Fragment key={`flight-${f.icao24}-${i}`}>
                  {trail && (
                    <>
                      <Polyline positions={trail.slice(0, 4)} pathOptions={{ color, weight: 2.5, opacity: 0.7 }} />
                      <Polyline positions={trail.slice(3, 8)} pathOptions={{ color, weight: 2, opacity: 0.35, dashArray: '6 4' }} />
                      <Polyline positions={trail.slice(7)} pathOptions={{ color, weight: 1.5, opacity: 0.12, dashArray: '3 6' }} />
                    </>
                  )}
                  <Marker position={[f.lat, f.lon]} icon={createMilitaryIcon(f, isFullscreen ? 32 : 26)}>
                    <Popup>
                      <div style={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, minWidth: 180 }}>
                        <strong style={{ fontSize: 13, color: '#ffffff' }}>{f.callsign || f.icao24}</strong><br />
                        <strong style={{ color }}>{f.type}</strong><br />
                        Origine: {f.origin || 'Inconnu'}<br />
                        Alt: {f.altitude?.toLocaleString()} ft | {f.speed} kts | {Math.round(f.heading)}&deg;
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })}

            {/* Satellite Layer */}
            {showLayers.satellites && satelliteData.length > 0 && (
              <SatelliteLayer satellites={satelliteData} theater={theaterKey} showTracks={true} />
            )}

            {/* AIS Maritime Vessel Layer */}
            {showLayers.vessels && vessels.vessels?.map((v, i) => {
              const vtype = (v.type || '').toLowerCase();
              const vesselColor = vtype.includes('tanker') ? '#22d3ee' : vtype.includes('cargo') || vtype.includes('bulk') ? '#60a5fa' : vtype.includes('military') || vtype.includes('naval') ? '#f87171' : '#a3e635';
              const heading = v.heading || v.cog || 0;
              return (
                <Marker key={`vessel-${i}`}
                  position={[v.lat, v.lon]}
                  icon={L.divIcon({
                    className: '',
                    html: `<div style="transform:rotate(${heading}deg);width:20px;height:20px;display:flex;align-items:center;justify-content:center;" title="${v.name || v.mmsi}">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="${vesselColor}" opacity="0.85">
                        <path d="M12 2L4 20h16L12 2z"/>
                      </svg>
                    </div>`,
                    iconSize: [20, 20],
                    iconAnchor: [10, 10],
                  })}>
                  <Popup>
                    <div style={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, minWidth: 200 }}>
                      <strong style={{ fontSize: 13, color: vesselColor }}>{v.name || `MMSI ${v.mmsi}`}</strong><br />
                      <strong>Type:</strong> {v.type?.toUpperCase()}{v.flag ? ` (${v.flag})` : ''}<br />
                      <strong>Cap:</strong> {heading}° | <strong>Vitesse:</strong> {v.speed_knots || v.speed || 0} kts<br />
                      {v.destination && <><strong>Dest:</strong> {v.destination}<br /></>}
                      {v.cargo_type && <><strong>Cargaison:</strong> {v.cargo_type}<br /></>}
                      {v.dwt && <><strong>DWT:</strong> {v.dwt?.toLocaleString()} t<br /></>}
                      {v.status && <><strong>Statut:</strong> {v.status}<br /></>}
                      {v.imo && <><strong>IMO:</strong> {v.imo}<br /></>}
                      <span style={{ color: v.data_source === 'TankerTrackers' ? '#fbbf24' : '#64748b', fontSize: 10 }}>
                        Source: {v.data_source === 'TankerTrackers' ? 'TankerTrackers (nom réel)' : v.data_source || 'AIS'}
                      </span>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Conflict Events Markers (IranWarMap-style) */}
            {showLayers.osintFeed && !replayMode && conflictMarkers.map((ev, i) => (
              <CircleMarker 
                key={`conflict-${i}`} 
                center={[ev.location.lat, ev.location.lon]}
                radius={ev.priority === 'high' ? 8 : 5}
                fillColor={ev.color}
                fillOpacity={0.6}
                stroke={true}
                color={ev.color}
                weight={ev.priority === 'high' ? 2 : 1}
                opacity={0.8}
              >
                <Popup>
                  <div style={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: 12, minWidth: 220 }}>
                    <strong style={{ fontSize: 13, color: ev.color }}>
                      {ev.event_type?.replace('_', ' ').toUpperCase()}
                    </strong><br />
                    <span style={{ fontWeight: 'bold', color: '#ffffff' }}>{ev.location.name}</span> ({ev.location.country})<br />
                    <span style={{ fontSize: 11 }}>{ev.title}</span><br />
                    <span style={{ color: '#64748b', fontSize: 10 }}>Source: {ev.source} ({ev.source_type})</span><br />
                    {ev.url && (
                      <a href={ev.url} target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8', fontSize: 10 }}>
                        Ouvrir la source
                      </a>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>

          {/* Stats overlay */}
          <div className="absolute bottom-2 right-2 z-[1000] flex gap-2">
            {(replayMode ? currentFlights.length : flights.military) > 0 && (
              <div className="bg-black/90 border border-sky-500/40 px-2 py-1 font-mono text-xs text-sky-400">
                <Plane className="w-3 h-3 inline mr-1" />{replayMode ? currentFlights.length : flights.military} aeronefs
              </div>
            )}
          </div>

          {/* ============ GRANDE HORLOGE MILITAIRE (Replay) ============ */}
          {replayMode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none" data-testid="replay-big-clock">
              <div className="flex flex-col items-center">
                <div className="bg-black/85 border-2 border-cyan-500/60 px-8 py-3 backdrop-blur-sm"
                     style={{ boxShadow: '0 0 30px rgba(0,200,255,0.15), inset 0 0 20px rgba(0,200,255,0.05)' }}>
                  <div className="font-mono text-cyan-300 text-5xl font-bold tracking-[0.15em] tabular-nums leading-none"
                       style={{ textShadow: '0 0 20px rgba(0,200,255,0.4), 0 0 40px rgba(0,200,255,0.15)' }}>
                    {replayCurrentTime ? new Date(replayCurrentTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--:--'}
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-1">
                    <span className="font-mono text-white/40 text-xs tracking-wider">
                      {replayCurrentTime ? new Date(replayCurrentTime).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : ''}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-cyan-500/50" />
                    <span className="font-mono text-amber-400/70 text-xs tracking-wider">ZULU</span>
                  </div>
                </div>
                <div className="mt-1 bg-black/70 border border-white/10 px-3 py-0.5 font-mono text-[10px] text-white/30 tracking-widest">
                  REPLAY {replayTimeWindow}H — {replaySecsPerHour}s/h — {replayFrame + 1}/{replayData?.adsb_frame_count || '?'}
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          {(replayMode || (showLayers.flights && flights.military > 0) || (showLayers.vessels && vessels.count > 0)) && (
            <div className="absolute bottom-2 left-2 z-[1000] bg-black/90 border border-white/10 px-2.5 py-1.5 font-mono text-[10px] space-y-0.5">
              <div className="text-white/30 uppercase tracking-wider mb-0.5">Legende</div>
              {(showLayers.flights) && LEGEND_ITEMS.map(item => (
                <div key={item.key} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 inline-block" style={{ backgroundColor: item.color, borderRadius: 1 }} />
                  <span style={{ color: item.color }}>{item.label}</span>
                </div>
              ))}
              {showLayers.vessels && vessels.count > 0 && (
                <>
                  <div className="text-white/20 border-t border-white/10 mt-1 pt-1 uppercase tracking-wider">AIS Maritime</div>
                  <div className="flex items-center gap-1.5"><span className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[7px] border-l-transparent border-r-transparent border-b-cyan-400 inline-block" /><span className="text-cyan-400">Tanker</span></div>
                  <div className="flex items-center gap-1.5"><span className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[7px] border-l-transparent border-r-transparent border-b-blue-400 inline-block" /><span className="text-blue-400">Cargo</span></div>
                  <div className="flex items-center gap-1.5"><span className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[7px] border-l-transparent border-r-transparent border-b-red-400 inline-block" /><span className="text-red-400">Militaire</span></div>
                  <div className="flex items-center gap-1.5"><span className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[7px] border-l-transparent border-r-transparent border-b-lime-400 inline-block" /><span className="text-lime-400">Autre</span></div>
                  <div className="text-amber-400/80 text-[9px] mt-1 font-bold uppercase">
                    {vessels.source_label?.includes('OSINT') || vessels.source_label?.includes('réel') 
                      ? vessels.source_label 
                      : 'Noms réels — Positions estimées'}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        {replayMode ? (
          <SourceConsole events={replayConsoleEvents} isFullscreen={isFullscreen} />
        ) : (showLayers.osintFeed || showLayers.telegram) && (
          <div className="w-80 border-l border-white/10 bg-black/90 flex flex-col min-h-0" data-testid="right-sidebar">
            {/* OSINT Conflict Feed */}
            {showLayers.osintFeed && (
              <div className="border-b border-white/10">
                <ConflictEventsFeed 
                  token={token} 
                  theater={theaterKey}
                  onEventsLoaded={setConflictMarkers}
                />
              </div>
            )}
            {/* Telegram Feed */}
            {showLayers.telegram && (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                  <span className="font-mono text-emerald-400 text-xs uppercase tracking-wider flex items-center gap-1">
                    <Radio className="w-3 h-3" /> Telegram Live
                  </span>
                  <span className="font-mono text-white/30 text-xs">{telegram.channels_scraped || 0}/{telegram.channels_total || 27}</span>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {telegram.posts?.slice(0, 50).map((post, i) => (
                    <a key={`tg-${i}`} href={post.url} target="_blank" rel="noopener noreferrer"
                      className="block px-3 py-2 border-b border-white/5 hover:bg-white/5 transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: post.color }} />
                        <span className="font-mono text-xs font-bold" style={{ color: post.color }}>{post.channel_label}</span>
                        <span className="font-mono text-white/25 text-xs ml-auto">
                          {post.date ? new Date(post.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <p className="font-mono text-white/70 text-xs leading-relaxed line-clamp-3">{post.text}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .leaflet-container { background: #0a0e1a !important; }
        .leaflet-popup-content-wrapper { background: #0f172a; color: #e2e8f0; border: 1px solid #334155; border-radius: 4px; }
        .leaflet-popup-tip { background: #0f172a; }
        .leaflet-popup-content { color: #e2e8f0 !important; }
        .leaflet-popup-close-button { color: #94a3b8 !important; font-size: 18px !important; }
        .military-aircraft-marker { display: flex; align-items: center; justify-content: center; }
        .leaflet-control-zoom { border: 1px solid #334155 !important; border-radius: 6px !important; overflow: hidden; }
        .leaflet-control-zoom a { background: #0f172a !important; color: #22d3ee !important; border-color: #334155 !important; font-size: 18px !important; width: 36px !important; height: 36px !important; line-height: 36px !important; }
        .leaflet-control-zoom a:hover { background: #1e293b !important; color: #ffffff !important; }
        .leaflet-control-attribution { display: none !important; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out both; }
        ${EXPLOSION_CSS}
        ${SATELLITE_CSS}
      `}</style>
    </div>
  );
}
